import React, { useMemo } from "react";
import { Card, Grid, Text } from "@jsq/ds/core";
import type { AgGridReact } from "@jsq/ds/core";
import { useGetAllInvestmentEntitiesQuery } from "../../../../graphql/supergraphHooks";
import { toDbId } from "../../../../utils/graphql_utils";
import { COMPLIANCE_PROGRAMS } from "../../constants";
import { defaultGridOptions } from "../gridOptions";
import {
  cardHeaderRow,
  clickableGridRow,
  gridWrapper,
  paginationLeft,
} from "../styles_vanilla.css";
import { getEntityColumnDefs } from "./entityColumnDefs";
import { useEntityDatasource } from "./useEntityDatasource";
import type { FilterEntity } from "./entityColumnDefs";
import type { EntityRowData } from "../types";

type Props = {
  arenaId: number;
  onEntityRowClick?: (entityId: number, entityName: string) => void;
  showPendingDivestment: boolean;
};

export const EntityTable: React.FC<Props> = ({
  arenaId,
  onEntityRowClick,
  showPendingDivestment,
}) => {
  const datasource = useEntityDatasource(arenaId);

  const { data } = useGetAllInvestmentEntitiesQuery({
    variables: { compliancePrograms: COMPLIANCE_PROGRAMS },
  });
  const filterEntities: FilterEntity[] = useMemo(
    () => data?.investmentEntities?.edges.map((e) => e.node) ?? [],
    [data],
  );

  const columnDefs = useMemo(
    () => getEntityColumnDefs({ filterEntities, showPendingDivestment }),
    [filterEntities, showPendingDivestment],
  );

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = useMemo(
    () => ({
      ...defaultGridOptions,
      rowModelType: "serverSide",
      serverSideDatasource: datasource,
      paginationPageSize: 20,
      paginationPageSizeSelector: false,
      getRowClass: onEntityRowClick ? () => clickableGridRow : undefined,
      onRowClicked: onEntityRowClick
        ? (event: { data: unknown }) => {
            const row = event.data as EntityRowData;
            if (row?.entityId) onEntityRowClick(toDbId(row.entityId), row.entityName);
          }
        : undefined,
    }),
    [datasource, onEntityRowClick],
  );

  return (
    <Card>
      <div className={cardHeaderRow}>
        <Text variant="h3">All entities</Text>
      </div>
      <div className={`${gridWrapper} ${paginationLeft}`}>
        <Grid<EntityRowData> columnDefs={columnDefs} gridOptions={gridOptions} />
      </div>
    </Card>
  );
};
